"use client";

import { format } from "date-fns";
import { useTranslations } from "next-intl";
import React from "react";

import AccountTypeIcon from "@/components/account-type-icon";
import BeneficiaryBankDetails from "@/components/beneficiary-bank-details";
import CollapsibleDetailSection from "@/components/collapsible-detail-section";
import DetailRow from "@/components/detail-row";
import FormattedCurrencyAmount from "@/components/formatted-currency-amount";
import { LEDGER_ENTRY_DATE_TIME_LONG_FORMAT } from "@/constants/dateTime";
import { useLedgerFieldLabel } from "@/utils/formatLedgerFieldLabel";
import { usePurposeCodeLabel } from "@/utils/formatPurposeCode";
import { getNestedProperty } from "@/utils/getNestedProperty";

import TransactionStatusBadge, {
  TransactionStatusBadgeType,
} from "../transaction-status-badge";

import LedgerEntryBaseFields from "./ledger-entry-base-fields";
import { MissingResolutionAlert } from "./missing-resolution-alert";

interface BankingDetailsProps {
  entry: LedgerEntryResolvedBankTransactions;
}

type FiatReasonObject = {
  directionTranslation: string;
  displayName?: string;
  hideDisplayName?: boolean;
  amountTranslation: string;
  transactionTimeTranslation: string;
  forceNegative: boolean;
  forcePositive: boolean;
  showBeneficiaryBankAccount: boolean;
  showReason?: boolean;
  reasonTranslation?: string;
  hideReference?: boolean;
};

const reasonObject: Record<string, FiatReasonObject> = {
  debit: {
    directionTranslation: "to",
    displayName: "resolvedRelatedPayment?.beneficiary?.name",
    amountTranslation: "amountSent",
    transactionTimeTranslation: "sentOn",
    forceNegative: true,
    forcePositive: false,
    showBeneficiaryBankAccount: true,
  },
  credit: {
    directionTranslation: "from",
    displayName: "debitBankAccount?.accountName",
    amountTranslation: "amountReceived",
    transactionTimeTranslation: "receivedOn",
    forceNegative: false,
    forcePositive: true,
    showBeneficiaryBankAccount: false,
  },
  "debit-rebate": {
    directionTranslation: "from",
    hideDisplayName: true,
    displayName: "debitBankAccount?.accountName",
    amountTranslation: "amountReceived",
    transactionTimeTranslation: "receivedOn",
    forceNegative: false,
    forcePositive: true,
    showBeneficiaryBankAccount: false,
    showReason: true,
    reasonTranslation: "debitRebate",
    hideReference: true,
  },
  "credit-rebate": {
    directionTranslation: "from",
    hideDisplayName: true,
    displayName: "debitBankAccount?.accountName",
    amountTranslation: "amountReceived",
    transactionTimeTranslation: "receivedOn",
    forceNegative: false,
    forcePositive: true,
    showBeneficiaryBankAccount: false,
    showReason: true,
    reasonTranslation: "creditRebate",
    hideReference: true,
  },
  "credit-deduction": {
    directionTranslation: "deduction",
    hideDisplayName: true,
    amountTranslation: "amountDeducted",
    transactionTimeTranslation: "deductedOn",
    forceNegative: true,
    forcePositive: false,
    showBeneficiaryBankAccount: false,
    hideReference: true,
  },
  "debit-deduction": {
    directionTranslation: "deduction",
    hideDisplayName: true,
    amountTranslation: "amountDeducted",
    transactionTimeTranslation: "deductedOn",
    forceNegative: true,
    forcePositive: false,
    showBeneficiaryBankAccount: false,
    hideReference: true,
  },
};

export default function BankingDetails({ entry }: BankingDetailsProps) {
  const tLedgerFields = useTranslations("ledger.fields");
  const tCommon = useTranslations("common");
  const tEnums = useTranslations("enums");
  const tBankAccount = useTranslations("bankAccount");
  // All hooks must run unconditionally — early-return guards live below them
  // (Rules of Hooks).
  const purposeCodeLabel = usePurposeCodeLabel();

  // `fieldLabel` guards DYNAMIC, backend-driven `ledger.fields.*` keys (from the
  // `reason` object) so an empty/missing key never reaches `t()`. The old
  // `tLedgerFields(reason?.X ?? "") || ""` pattern did NOT prevent the
  // `MISSING_MESSAGE: ledger.fields.` Sentry event (CUSTOMER-UI-6M): next-intl's
  // `onError` fires *before* `t()` returns the fallback. STATIC-literal calls
  // (`tLedgerFields("status")`, `"reason"`, …) are safe and stay direct.
  const fieldLabel = useLedgerFieldLabel();

  const reason = reasonObject[entry.reason as keyof typeof reasonObject];

  // BFF resolution failed (graceful degradation). Show base ledger fields
  // plus a support recovery prompt rather than letting the dot-walks below
  // throw and trigger the generic ClientErrorBoundary fallback.
  if (!entry.resolvedData) {
    return (
      <div className="space-y-0">
        <LedgerEntryBaseFields entry={entry} />
        <MissingResolutionAlert />
      </div>
    );
  }

  const resolvedRelatedPayment = entry.resolvedData.resolvedRelatedPayment;

  return (
    <div className="space-y-0">
      {!reason?.hideDisplayName && (
        <DetailRow
          label={
            tCommon(reason?.directionTranslation ?? "") || entry.reason || ""
          }
          value={
            getNestedProperty(
              entry.resolvedData as unknown as Record<string, unknown>,
              reason?.displayName ?? ""
            ) as string | undefined
          }
          translateValue={false}
        />
      )}
      <DetailRow
        label={fieldLabel(reason?.amountTranslation)}
        value={
          <FormattedCurrencyAmount
            balance={entry.balance ?? 0}
            product={entry.product}
            forceNegative={reason?.forceNegative}
            forcePositive={reason?.forcePositive}
          />
        }
        translateValue={false}
      />
      {!!resolvedRelatedPayment?.feeAmount && (
        <>
          <DetailRow
            label={tLedgerFields("transactionFee")}
            value={
              <FormattedCurrencyAmount
                balance={resolvedRelatedPayment.feeAmount ?? 0}
                product={resolvedRelatedPayment.feeCurrency ?? ""}
                forceNegative
              />
            }
            translateValue={false}
          />
        </>
      )}

      <DetailRow
        label={tLedgerFields("status")}
        value={
          <TransactionStatusBadge
            size="xs"
            status={
              (entry.resolvedData.transactionState ??
                "PROCESSING") as TransactionStatusBadgeType
            }
            showReconciled
            mobileSize="sm"
          />
        }
      />

      <DetailRow
        label={fieldLabel(reason?.transactionTimeTranslation)}
        value={format(
          new Date(entry.resolvedData.transactionTime || ""),
          LEDGER_ENTRY_DATE_TIME_LONG_FORMAT
        )}
      />

      {reason?.showReason && (
        <DetailRow
          label={tLedgerFields("reason")}
          value={fieldLabel(reason?.reasonTranslation)}
        />
      )}

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("purposeCode")}
        value={purposeCodeLabel(resolvedRelatedPayment?.paymentReason) ?? "-"}
      />

      {!reason?.hideReference && (
        <DetailRow
          label={tLedgerFields("senderReference")}
          value={
            entry.reason === "credit"
              ? (entry.resolvedData.narrative?.[0] ?? "-")
              : (resolvedRelatedPayment?.remittanceInformation ?? "-")
          }
          translateValue={false}
        />
      )}

      {!!entry.resolvedData.narrative?.length && (
        <>
          <hr className="my-2 opacity-65" />
          <CollapsibleDetailSection
            title={tLedgerFields("narrative")}
            showLabel={tLedgerFields("viewNarrative")}
            hideLabel={tLedgerFields("hideNarrative")}
          >
            <code className="mt-1 text-sm font-mono bg-brand-primary/10 p-2 rounded-md break-all leading-[2.5] whitespace-pre-line block">
              {entry.resolvedData.narrative?.join("\n") ?? ""}
            </code>
          </CollapsibleDetailSection>
        </>
      )}

      <hr className="my-2 opacity-65" />

      {reason?.showBeneficiaryBankAccount &&
        resolvedRelatedPayment?.beneficiary && (
          <>
            <CollapsibleDetailSection
              title={tBankAccount("header")}
              showLabel={tBankAccount("viewDetails")}
              hideLabel={tBankAccount("hideDetails")}
            >
              <div className="space-y-0">
                <DetailRow
                  label={tLedgerFields("payeeType")}
                  value={
                    <AccountTypeIcon
                      type={
                        resolvedRelatedPayment?.beneficiary?.beneficiaryType ||
                        ""
                      }
                      label={tEnums(
                        `BeneficiaryType.${resolvedRelatedPayment?.beneficiary?.beneficiaryType}`
                      )}
                      showLabel={true}
                      size={14}
                    />
                  }
                />
                <BeneficiaryBankDetails
                  beneficiary={resolvedRelatedPayment?.beneficiary}
                  singleColumn
                />
              </div>
            </CollapsibleDetailSection>
            <hr className="my-2 opacity-65" />
          </>
        )}

      <DetailRow
        label={tLedgerFields("referenceId")}
        value={entry.resolvedData.reference}
        translateValue={false}
      />
    </div>
  );
}
